import React, { useState, useEffect } from 'react';
import { Card, Select, Alert, Typography, Row, Col, Statistic, Space, Skeleton } from 'antd';
import { 
  LineChartOutlined,
  NumberOutlined,
  UserOutlined,
  LoadingOutlined 
} from '@ant-design/icons';
import { TrendLineChart } from './TrendLineChart';
import { API_BASE_URL } from '../../../../constants/config';
import { getAndSanitize } from '../../../../shared/utils/sanitization';


const { Text } = Typography;
const { Option } = Select;

interface TrendDataPoint {
  date: string;
  numeros: number;
  usuarios: number;
  cargas: number;
}

interface ExternalAnalyticsTrendData {
  trend: TrendDataPoint[];
  summary: {
    totalNumeros: number;
    totalUsuarios: number;
    totalCargas: number;
    avgDailyNumeros: number;
    avgDailyUsuarios: number;
    avgDailyCargas: number;
  };
}

interface ExternalAnalyticsTrendChartProps {
  dateRange?: [string, string] | null;
  adminId?: string;
  loading?: boolean;
}

export const ExternalAnalyticsTrendChart: React.FC<ExternalAnalyticsTrendChartProps> = ({
  dateRange,
  adminId,
  loading: propLoading = false
}) => {
  const [data, setData] = useState<ExternalAnalyticsTrendData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'all' | 'numeros' | 'usuarios' | 'cargas'>('all');

  const fetchExternalAnalyticsTrend = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAndSanitize("token");
      if (!token) {
        throw new Error("Token no disponible");
      }

      const params = new URLSearchParams();
      if (dateRange) {
        // Usar directamente las fechas ISO del filtro de Analytics
        params.append("dateFrom", dateRange[0]);
        params.append("dateTo", dateRange[1]);
      }
      if (adminId) {
        params.append("adminId", adminId);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/external-analytics/trend?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setData(result.data);
      } else {
        console.error("ExternalAnalyticsTrendChart - API Error:", result);
        throw new Error(result.message || "Error al obtener datos de tendencia");
      }
    } catch (err: any) {
      console.error("Error fetching external analytics trend:", err);
      setError(err.message || "Error al cargar tendencia de external analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExternalAnalyticsTrend();
  }, [dateRange, adminId]);

  const prepareChartData = () => {
    if (!data) return [];

    if (activeView === 'all') {
      const chartData: any[] = [];
      
      data.trend.forEach(point => {
        chartData.push(
          {
            date: point.date,
            value: point.numeros,
            type: 'Números'
          },
          {
            date: point.date,
            value: point.usuarios,
            type: 'Usuarios'
          },
          {
            date: point.date,
            value: point.cargas,
            type: 'Cargas'
          }
        );
      });

      return chartData;
    } else {
      const chartData = data.trend.map(point => {
        let value: number;
        let type: string;
        
        switch (activeView) {
          case 'numeros':
            value = point.numeros;
            type = 'Números';
            break;
          case 'usuarios':
            value = point.usuarios;
            type = 'Usuarios';
            break;
          case 'cargas':
            value = point.cargas;
            type = 'Cargas';
            break;
          default:
            value = 0;
            type = '';
        }
        
        return {
          date: point.date,
          value,
          type
        };
      });
      return chartData;
    }
  };

  const hasData = () => {
    return data && data.trend && data.trend.length > 0;
  };

  const formatNumber = (num: number, decimals: number = 1): string => {
    return String(Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals));
  };

  if (error) {
    return (
      <Card title="Tendencia de External Analytics">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <LineChartOutlined />
          <span>Tendencia Promedio de External Analytics</span>
        </Space>
      }
      extra={
        <Select
          value={activeView}
          onChange={setActiveView}
          style={{ width: 160 }}
        >
          <Option value="all">Todos los Datos</Option>
          <Option value="numeros">Solo Números</Option>
          <Option value="usuarios">Solo Usuarios</Option>
          <Option value="cargas">Solo Cargas</Option>
        </Select>
      }
    >
      {(loading || propLoading) ? (
        <div style={{ padding: '16px' }}>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
          </Row>
          <Card>
            <Skeleton active paragraph={{ rows: 8 }} />
          </Card>
        </div>
      ) : hasData() ? (
        <>
          {/* Estadísticas resumen */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title="Total Números"
                  value={data.summary.totalNumeros}
                  prefix={<NumberOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Promedio: {formatNumber(data.summary.avgDailyNumeros)}/día
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title="Total Usuarios"
                  value={data.summary.totalUsuarios}
                  prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Promedio: {formatNumber(data.summary.avgDailyUsuarios)}/día
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title="Total Cargas"
                  value={data.summary.totalCargas}
                  prefix={<LoadingOutlined style={{ color: '#fa8c16' }} />}
                  valueStyle={{ color: '#fa8c16' }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Promedio: {formatNumber(data.summary.avgDailyCargas)}/día
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Información adicional */}
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Alert
                message="Información sobre las métricas"
                description={
                  <div>
                    <p><strong>Números:</strong> Registros de acceso externo del día</p>
                    <p><strong>Usuarios:</strong> Usuarios nuevos de PanelUser creados el día que coincidan con los userIds</p>
                    <p><strong>Cargas:</strong> LoadRequest con status "COMPLETED" y type "LOAD" del día, de esos usuarios nuevos</p>
                  </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>
          </Row>
          
          {/* Gráfico de tendencia */}
          <TrendLineChart
            data={prepareChartData()}
            xField="date"
            yField="value"
            seriesField={activeView === 'all' ? 'type' : undefined}
            height={400}
            smooth={true}
          />

          {/* Período de datos */}
          {data.trend.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary">
                Período: {data.trend[0].date} - {data.trend[data.trend.length - 1].date} 
                ({data.trend.length} días)
              </Text>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Typography.Text type="secondary">No hay datos disponibles</Typography.Text>
        </div>
      )}
    </Card>
  );
}; 